import type { IRepositoryParameters } from "@/wc/registry/lib/service/repository/types";
import type IURLSegmentClaimer from "@/wc/registry/types/url-segment-claimer";

// Creates a proxy constructor function, which returns a proxy that can
// be used to query for specific items in a specific context.
//
// E.g. calling this function with createProxyConstructor("action"), returns
// a Proxy, which can then be used in CustomElement classes to query all it's
// child nodes with the "data-proxy-action" attribute. All base classes use this
// proxy, for an example, see src/components/base.ts.
//
// The proxy creates a map to allow components to easily access their child
// nodes without having to build a querySelector every time. E.g. components
// can use this.actions.someSelector when it has a child node with
// <div data-proxy-action="someSelector"></div> to select it.
function createProxyConstructor(type: string) {
    return function <T extends HTMLElement>(context: T) {
        return new Proxy(new Map(), {
            get(obj, property: string) {
                if (!obj.has(property)) {
                    const toSet: HTMLElement = context.querySelector(
                        `[data-proxy-${type}=${property}]`
                    );

                    // In some rare cases, a check is done for the existence of an
                    // element while the template is not yet connected. Because of this,
                    // it is impossible for the proxy constructor to find an element
                    // through the querySelector. When it does not exist, we avoid
                    // setting undefined by returning early.
                    if (!toSet) {
                        return undefined;
                    }

                    obj.set(property, toSet);
                }
                return obj.get(property);
            },
            set(obj, property: string, value) {
                if (typeof value === "string") {
                    obj.set(
                        property,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore Allow implicit conversion.
                        context.querySelector(
                            `[data-proxy-${type}=${property}]`
                        )
                    );
                } else if (value.nodeType) {
                    obj.set(property, value);
                }
                return true;
            },
            deleteProperty(obj, property) {
                return obj.delete(property);
            },
        });
    };
}

export function empty(node: Element) {
    while (node.firstChild) {
        node.removeChild(node.firstChild);
    }
}

export function hide(elementOrElementList: Element | Element[]) {
    show(elementOrElementList, false);
}

// Takes either a list of elements or a single element, and
// shows or hides them by manipulating its style.
export function show(elementOrElementList: Element | Element[], show = true) {
    let nodes = [];
    if (
        Object.prototype.toString.call(elementOrElementList) ===
        "[object NodeList]"
    ) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Allow overload not matching, because it does match!
        nodes = [...Array.from(elementOrElementList)];
    } else {
        nodes = [elementOrElementList];
    }

    nodes.forEach((node) => {
        if (show) {
            node.style.display = "";
        } else {
            node.style.display = "none";
        }
    });
}

// Gets the parent or host of a node. E.g. when you have the following:
// <div><c-router></c-router></div>, the parent of c-router will be <div>.
// When wrapped in the shadow dom, node.host returns the shadowRoot.
export function getParentOrHost(node: Node | ShadowRoot): Node | ShadowRoot {
    return node.parentNode || (<ShadowRoot>node).host;
}

// Using a type predicate to narrow down the type to make sure that the passed
// node is actually the custom element that we're looking for.
// https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
export function isMatchingElement(
    node: Node | HTMLElement | ShadowRoot,
    tagName: string
): node is HTMLElement {
    if (!node) {
        return false;
    }

    if ((node as HTMLElement).tagName === undefined) {
        return false;
    }

    return (node as HTMLElement).tagName === tagName.toUpperCase();
}

// For a given node, move up the tree until the a node with the given tagName
// is found, and return it. This is e.g. used for form-inputs that want to
// configure themselves.
export function getFirstMatchingParentElement(
    context: Node,
    tagNameOrTagNames: string | string[],
    elementShouldHaveTheseKeysOnDataset: string[] = []
): HTMLElement | undefined {
    const matchFor = Array.isArray(tagNameOrTagNames)
        ? tagNameOrTagNames
        : [tagNameOrTagNames];
    let matchingElement: HTMLElement | undefined;
    let currentNode = context;
    while (currentNode) {
        currentNode = getParentOrHost(currentNode);
        if (
            matchFor.some(
                (tagName) =>
                    isMatchingElement(currentNode, tagName) &&
                    elementShouldHaveTheseKeysOnDataset.every(
                        (key) => key in currentNode.dataset
                    )
            )
        ) {
            matchingElement = currentNode as HTMLElement;
            break;
        }
    }
    return matchingElement;
}

// Removes a "data-" prefix from an attribute if it exists.
function getNameWithoutDataPrefix(name: string) {
    if (name.includes("data-")) {
        return name.substring(5);
    }

    return name;
}

// For a given element, try to extract the given attributes.
export function extractRepositoryParametersFromElement(
    element: HTMLElement,
    parametersToFind: string[]
): IRepositoryParameters {
    if (!element.hasAttributes) {
        return {};
    }

    const parametersAsAttributes = parametersToFind.map((p) =>
        p.replaceAll("_", "-")
    );
    const matchingAttributes = [...element.attributes].filter((attr: Attr) =>
        parametersAsAttributes.includes(getNameWithoutDataPrefix(attr.name))
    );

    // Takes a list of attributes which have their names ending in -id, and
    // convert them into an object. E.g. [{name: 'data-producer-id', value: '5'}]
    // gets turned into { 'producer_id': 5 }.
    const repositoryParameters = matchingAttributes.reduce((prev, attr) => {
        const attributeNameWithoutData = getNameWithoutDataPrefix(attr.name);
        const attributeNameAsParameter = attributeNameWithoutData.replaceAll(
            "-",
            "_"
        );
        prev[attributeNameAsParameter] = attr.value;
        return prev;
    }, {});

    if ("dataset" in element && element.dataset.serviceRepositoryParameters) {
        return {
            ...repositoryParameters,
            ...JSON.parse(element.dataset.serviceRepositoryParameters),
        };
    }

    return repositoryParameters;
}

// Determine what segments in the url are yet unclaimed. Some components
// contain information about what fragments in the url they claim, by
// traversing up in the DOM tree. By doing so, this function returns the
// index of the array of url fragments, where the fragments after the index
// are still unclaimed.
export async function getUnclaimedURLSegmentsStartIndex(context: Node) {
    let currentNode: IURLSegmentClaimer | Node = context;
    let index: number | Promise<number> = 0;
    while (currentNode) {
        currentNode = getParentOrHost(currentNode);
        if (currentNode && "getLastClaimedURLFragmentIndex" in currentNode) {
            // At this point we're sure that the currentNode implements
            // IURLSegmentClaimer, since lastURLFragmentIndex is undefined and is
            // only used by components implementing IURLSegmentClaimer.
            if ("isFinishedClaimingURLSegments" in currentNode) {
                await (<IURLSegmentClaimer>(<unknown>currentNode))
                    .isFinishedClaimingURLSegments;
            }
            index = (<IURLSegmentClaimer>(
                (<unknown>currentNode)
            )).getLastClaimedURLFragmentIndex();
            break;
        }
    }

    return index;
}

export const NodesProxy = createProxyConstructor("selector");
export const ActionsProxy = createProxyConstructor("action");
